import { memo, useMemo } from 'react'

import { VideocamOutlined, PlayArrowOutlined, CampaignOutlined, SportsScoreOutlined } from '@mui/icons-material'
import { Box, colors, SxProps, Theme, Tooltip, Typography } from '@mui/material'
import { fromUnixTime } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { formatBlockDuration, formatUnixTime } from 'domains/Workspaces/Events/Event/Watch/AdPods/utils'
import { ThemeTypes } from 'stores/settings'

const sharedBoxStyles: SxProps<Theme> = {
  minHeight: 50,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexGrow: 1,
  borderRadius: 1,
  p: 1,
}

export type AdItemProps = {
  variant: 'eventStart' | 'eventEnd' | 'content' | 'ad'
  start?: number
  duration?: number
  timestampLabel?: string
}

const AdItemRaw = ({ variant, start, duration, timestampLabel }: AdItemProps) => {
  const { t } = useTranslation(['entity', 'domain'])

  const itemData = useMemo(() => {
    switch (variant) {
      case 'ad':
        return {
          color: colors.green,
          label: t('domain:Workspace.Event.adPod'),
          icon: <CampaignOutlined fontSize="small" />,
        }
      case 'eventStart':
        return {
          color: colors.blueGrey,
          label: t('domain:Workspace.Event.eventStart'),
          icon: <PlayArrowOutlined fontSize="small" />,
        }
      case 'eventEnd':
        return {
          color: colors.blueGrey,
          label: t('domain:Workspace.Event.eventEnd'),
          icon: <SportsScoreOutlined fontSize="small" />,
        }
      case 'content':
      default:
        return {
          color: colors.grey,
          label: t('domain:Workspace.Event.content'),
          icon: <VideocamOutlined fontSize="small" />,
        }
    }
  }, [t, variant])

  const tooltipTitle = useMemo(() => {
    if (!start) return t('domain:Workspace.Event.unknown')

    const startLabel = fromUnixTime(start).toUTCString()
    const endLabel = duration ? fromUnixTime(start + duration).toUTCString() : t('domain:Workspace.Event.unknown')

    return variant === 'eventStart' || variant === 'eventEnd' ? startLabel : `${startLabel} - ${endLabel}`
  }, [duration, start, t, variant])

  return (
    <Tooltip placement="left" arrow title={tooltipTitle}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {variant === 'ad' || variant === 'content' ? (
          <Typography sx={{ minWidth: 50, textAlign: 'center' }}>{timestampLabel ? timestampLabel : null}</Typography>
        ) : null}
        <Box
          sx={{
            backgroundColor: (theme) => itemData.color[theme.palette.mode === ThemeTypes.DARK ? 700 : 300],
            color: (theme) =>
              theme.palette.getContrastText(itemData.color[theme.palette.mode === ThemeTypes.DARK ? 700 : 300]),
            ...sharedBoxStyles,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="heading-sm">
              {start ? `${formatUnixTime(start)}` : `[${t('domain:Workspace.Event.unknown')}]`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {itemData.icon}
              <Typography>{itemData.label}</Typography>
            </Box>
          </Box>
          <Typography>{duration ? formatBlockDuration(duration) || '0 sec' : null}</Typography>
        </Box>
      </Box>
    </Tooltip>
  )
}

export const AdItem = memo(AdItemRaw)
